import { useEffect, useRef, useState } from "react";
import { useForm, usePage } from "@inertiajs/react";
import { color, font, radius, shadow, type } from "@/lib/design-tokens";
import { trackEvent } from "@/lib/analytics";
import {
  newsletterConfirmationCopy,
  newsletterConfirmationFrom,
  newsletterPromptShownThisSession,
  newsletterPromptSuppressed,
  rememberNewsletterDismissal,
  rememberNewsletterPromptShown,
  rememberNewsletterSubscription,
  type NewsletterConfirmation,
} from "@/lib/newsletter";

const ARM_DELAY_MS = 5000;
const SOURCE = "exit-intent-newsletter";

/**
 * A restrained desktop exit-intent prompt.
 *
 * It waits for real page engagement, responds only to a mouse leaving through
 * the top of the viewport, appears once per browser session, and stays away for
 * 30 days after dismissal. Touch devices have no honest exit-intent signal, so
 * they keep the footer signup instead of getting a timed interruption.
 */
export function ExitIntentNewsletter() {
  const { url, flash } = usePage();
  const pathname = url.split(/[?#]/, 1)[0];
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState<NewsletterConfirmation | null>(null);
  const serverConfirmation = newsletterConfirmationFrom(flash, SOURCE);
  const dialog = useRef<HTMLDivElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const { data, setData, post, processing, errors, reset } = useForm({
    firstName: "Newsletter",
    lastName: "Subscriber",
    email: "",
    inquiryType: "newsletter",
    message: "Please add me to occasional shop updates.",
    source: SOURCE,
    is_robot: "",
  });

  useEffect(() => setOpen(false), [pathname]);

  // The stored-signup confirmation comes from the server's flash, so it shows
  // even when the page fully reloaded between submit and response (the modal
  // was unmounted and its local state lost). Declared after the pathname
  // effect so a fresh mount ends open.
  const confirmationKey = serverConfirmation && `${serverConfirmation.status}:${serverConfirmation.email}`;
  useEffect(() => {
    if (!serverConfirmation) return;
    rememberNewsletterSubscription();
    setConfirmed(serverConfirmation);
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationKey]);

  // Deterministic visual QA without waiting for a real pointer exit. The flag
  // only opens the same prompt early; it cannot submit, bypass suppression for
  // normal visitors, or change server behavior.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("__exitpreview")) return;
    rememberNewsletterPromptShown();
    previousFocus.current = document.activeElement as HTMLElement | null;
    setOpen(true);
  }, [url]);

  useEffect(() => {
    if (["/book", "/contact"].includes(pathname)) return;
    if (!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches) return;
    if (newsletterPromptSuppressed()) return;

    let armed = false;
    const armTimer = window.setTimeout(() => { armed = true; }, ARM_DELAY_MS);
    const onMouseOut = (event: MouseEvent) => {
      if (!armed || newsletterPromptShownThisSession() || event.relatedTarget || event.clientY > 0) return;
      rememberNewsletterPromptShown();
      previousFocus.current = document.activeElement as HTMLElement | null;
      setOpen(true);
      trackEvent("newsletter_exit_intent_open", { page_path: pathname });
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    (dialog.current?.querySelector<HTMLInputElement>('input[type="email"]') ?? dialog.current)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismiss();
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const focusable = Array.from(
        dialog.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), a[href]')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus.current?.focus();
    };
  }, [open]);

  // The form is replaced by the confirmation on success; move focus to the
  // dialog so it is not left on a removed input (and so a reload-opened
  // confirmed dialog still owns focus).
  useEffect(() => {
    if (open && confirmed) dialog.current?.focus();
  }, [open, confirmed]);

  const dismiss = () => {
    rememberNewsletterDismissal();
    setOpen(false);
    trackEvent("newsletter_exit_intent_dismiss", { page_path: pathname });
  };

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    post("/contact", {
      preserveScroll: true,
      onSuccess: (page) => {
        rememberNewsletterSubscription();
        const submitted = data.email.trim();
        reset();
        setConfirmed(newsletterConfirmationFrom(page.flash, SOURCE) ?? { status: "subscribed", email: submitted, source: SOURCE });
        trackEvent("newsletter_signup", { surface: "exit_intent", page_path: pathname });
      },
    });
  };

  if (!open) return null;

  return (
    <div
      className="bluebird bb-exit-intent-overlay"
      data-exit-intent-newsletter=""
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) dismiss();
      }}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-intent-title"
        className="bb-exit-intent-modal"
        tabIndex={-1}
        style={{ background: color.white, borderRadius: radius.panel, boxShadow: shadow.visitCard }}
      >
        <button type="button" className="bb-exit-intent-close" aria-label="Close shop updates signup" onClick={dismiss}>
          <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        <div style={{ fontFamily: font.mono, fontSize: "11px", letterSpacing: ".13em", textTransform: "uppercase", color: color.blue }}>
          Before you go
        </div>
        <h2 id="exit-intent-title" style={{ margin: "10px 0 0", fontSize: type.h3, lineHeight: 1.12, color: color.ink }}>
          Get occasional shop updates
        </h2>
        <p style={{ margin: "12px 0 0", maxWidth: "48ch", fontSize: "16px", lineHeight: 1.58, color: color.inkMuted }}>
          We send an occasional email when bikes, Colorado rebate windows, or shop events change.
        </p>

        {confirmed ? (
          <div role="status" aria-live="polite" data-newsletter-confirmation={confirmed.status} style={{ marginTop: "24px", color: color.blue, fontWeight: 800, fontSize: "18px" }}>
            {newsletterConfirmationCopy(confirmed).heading}
            <div style={{ marginTop: "6px", color: color.inkMuted, fontSize: "15px", fontWeight: 400, overflowWrap: "anywhere" }}>
              {newsletterConfirmationCopy(confirmed).body}
            </div>
          </div>
        ) : (
          <form action="/contact" method="post" onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "24px" }}>
            <input type="hidden" name="firstName" value={data.firstName} />
            <input type="hidden" name="lastName" value={data.lastName} />
            <input type="hidden" name="inquiryType" value={data.inquiryType} />
            <input type="hidden" name="message" value={data.message} />
            <input type="hidden" name="source" value={data.source} />
            <input type="text" name="is_robot" value={data.is_robot} onChange={(event) => setData("is_robot", event.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px" }} />
            <input
              type="email"
              required
              name="newsletterEmail"
              value={data.email}
              onChange={(event) => setData("email", event.target.value)}
              placeholder="you@email.com"
              aria-label="Email address"
              className="bb-input"
              style={{
                flex: "1 1 230px",
                minWidth: 0,
                fontFamily: "inherit",
                fontSize: "16px",
                color: color.ink,
                background: color.white,
                border: `1.5px solid ${color.borderCard}`,
                borderRadius: radius.button,
                padding: "14px 16px",
                outline: "none",
              }}
            />
            <button type="submit" disabled={processing} className="bb-btn bb-btn-gold" style={{ border: 0, fontFamily: "inherit", fontSize: "15px", padding: "15px 22px", cursor: "pointer" }}>
              {processing ? "Adding…" : "Get occasional updates"}
            </button>
            {errors.email && <div role="alert" style={{ width: "100%", color: color.soldRed, fontSize: "14px" }}>{errors.email}</div>}
          </form>
        )}
      </div>
    </div>
  );
}
